import { Box } from "@mui/material"
import { useCallback, useEffect, useState } from "react"
import { FilterComparator, ReserveClubhouseRequestData, ReserveClubhouseRequestDataApi, ReserveClubhouseRequestsApi, ResponseError } from "../../clients"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { getConfiguration } from "../../clients/configuration"
import { addAlertWithTimeout } from "../../redux/features/alerts/alertsHelpers"
import { AppAlert } from "../AppAlerts/AppAlerts"
import { MyClubhouseReservationRequestsTable } from "./MyClubhouseReservationRequestsTable"
import { MyClubhouseReservationRequestDialog } from "./MyClubhouseReservationRequestDialog"

export const MyAccountClubhouseReservationRequests = () => {

    const dispatch = useAppDispatch()
    const user = useAppSelector(state => state.currentUser.account)
    
    const [requests, setRequests] = useState<ReserveClubhouseRequestData[]>([])
    const [selectedRequest, setSelectedRequest] = useState<ReserveClubhouseRequestData | null>(null)

    const handleViewRequest = (request: ReserveClubhouseRequestData) => {
        setSelectedRequest(request)
    }

    const handleDeleteRequest = async (request: ReserveClubhouseRequestData) => {
        const config = await getConfiguration()
        const reserveClubhouseRequestDataApi = new ReserveClubhouseRequestsApi(config)
        await reserveClubhouseRequestDataApi.deleteClubhouseReservationRequest({
            requestID: request.reserveClubhouseRequestId
        }).then(() => {
            const appAlert = new AppAlert('info', 'Request deleted')
            addAlertWithTimeout(dispatch, appAlert)
            setSelectedRequest(null)
            loadRequests().then((data) => setRequests(data))
        }).catch(async (err) => {
            const responseErr = err as ResponseError
            if (responseErr.response.status === 409) {
                const text = await responseErr.response.text().catch(() => '')
                const appAlert = new AppAlert('error', text)
                addAlertWithTimeout(dispatch, appAlert, 3000)
            }
            const appAlert = new AppAlert('error', 'Something went wrong. Try again later.')
            addAlertWithTimeout(dispatch, appAlert, 3000)
        })
    }

    const loadRequests = useCallback(async () => {
        const config = await getConfiguration()
        const reserveClubhouseRequestDataApi = new ReserveClubhouseRequestDataApi(config)
        const requests = await reserveClubhouseRequestDataApi.listClubhouseRequestDataForAccount({
            accountID: user?.id || '',
            filters: [
                {
                    field: 'reserve_clubhouse_request_deleted',
                    value: false,
                    comparator: FilterComparator.Equal,
                },
                {
                    field: 'reserve_clubhouse_request_begin_date',
                    value: new Date(),
                    comparator: FilterComparator.GreaterThanOrEqualTo,
                }
            ],
            order: [
                { key: 'reserve_clubhouse_request_begin_date', order: 'ASC' }
            ]
        }).catch(async (err) => {
            const responseErr = err as ResponseError
            if (responseErr.response.status === 409) {
                const text = await responseErr.response.text().catch(() => '')
                const appAlert = new AppAlert('error', text)
                addAlertWithTimeout(dispatch, appAlert, 3000)
            }
            const appAlert = new AppAlert('error', 'Something went wrong. Try again later.')
            addAlertWithTimeout(dispatch, appAlert, 3000)
            return { data: [] }
        })
        return requests.data
    }, [dispatch, user])

    useEffect(() => {
        loadRequests().then((data) => setRequests(data))
    }, [loadRequests])

    return (
        <Box sx={{ width: '100%' }}>
            <MyClubhouseReservationRequestsTable requests={requests} handleViewRequest={handleViewRequest} />
            <MyClubhouseReservationRequestDialog
                open={selectedRequest !== null}
                handleClose={() => setSelectedRequest(null)}
                request={selectedRequest}
                handleDeleteRequest={handleDeleteRequest}
            />
        </Box>
    )
}